"use client";

import React from "react";

// Shared structural skeleton that mirrors the screener grid used by the Radar
// (MiniPlaybookWidget) and Hub (MiniPlaybookWidgetHub) tables: frozen columns
// (ticker / flag / industry / name) + metric columns + data rows.
//
// Layout: CSS grid. Frozen columns keep fixed widths; metric columns use
// `minmax(width, 1fr)` so they STRETCH to fill any leftover width on wide
// screens (no empty gap on the right) yet never shrink below their natural
// width — when there are enough columns to exceed the container the grid
// overflows and scrolls horizontally, exactly like the live table. Rendering
// the same shape while data loads keeps the swap to real content smooth and
// shift-free (same approach as the Portfolios skeletons).

export type ScreenerSkeletonCol = { width: number; isSpacer?: boolean };

const HEADER_H = 28;
const SUB_HEADER_H = 42;
const ROW_H = 36;
const ROW_BORDER = "1px solid rgba(255,255,255,0.04)";

export default function ScreenerTableSkeleton({
  isMobile = false,
  showPortfolio = false,
  portfolioW = 20,
  tickerW = 55,
  flagW = 15,
  industryW = 15,
  nameW,
  columns,
  columnCount = 9,
  rowCount = 12,
  background = "#181818",
}: {
  isMobile?: boolean;
  showPortfolio?: boolean;
  portfolioW?: number;
  tickerW?: number;
  flagW?: number;
  industryW?: number;
  nameW?: number;
  /** Real column widths when available (widget usage). */
  columns?: ScreenerSkeletonCol[];
  /** Fallback column count when real widths are unknown (page first-load). */
  columnCount?: number;
  rowCount?: number;
  background?: string;
}) {
  const resolvedNameW = nameW ?? (isMobile ? 100 : 180);

  const frozen: number[] = [];
  if (showPortfolio) frozen.push(portfolioW);
  frozen.push(tickerW, flagW, industryW, resolvedNameW);
  const frozenCount = frozen.length;

  const metricCols: ScreenerSkeletonCol[] =
    columns && columns.length
      ? columns
      : Array.from({ length: Math.max(1, columnCount) }, () => ({ width: 55 }));

  // Frozen columns fixed; metric columns grow to fill leftover space.
  const gridTemplate = [
    ...frozen.map((w) => `${w}px`),
    ...metricCols.map((c) => `minmax(${c.width}px, 1fr)`),
  ].join(" ");

  const tickerIdx = showPortfolio ? 1 : 0;
  const nameIdx = showPortfolio ? 4 : 3;

  const bar = (w: number | string, h = 10, r = 4) => (
    <div className="bastion-skeleton" style={{ width: w, height: h, borderRadius: r }} />
  );

  const renderFrozenRowCells = () => {
    const cells: React.ReactNode[] = [];
    if (showPortfolio) {
      cells.push(<div key="pf" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }} />);
    }
    cells.push(
      <div key="tk" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{bar(32, 9)}</div>,
      <div key="fl" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{bar(12, 12, 3)}</div>,
      <div key="in" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>{bar(12, 12, 3)}</div>,
    );
    return cells;
  };

  return (
    <div style={{ background, width: "100%" }}>
      <div style={{ display: "grid", gridTemplateColumns: gridTemplate, width: "100%" }}>
        {/* Group header row */}
        <div style={{ gridColumn: `1 / span ${frozenCount}`, height: HEADER_H }} />
        {metricCols.map((c, i) => (
          <div key={`gh-${i}`} style={{ height: HEADER_H, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!c.isSpacer && i % 3 === 0 ? bar("70%", 7) : null}
          </div>
        ))}

        {/* Column header row */}
        {frozen.map((_, i) => (
          <div
            key={`chf-${i}`}
            style={{
              height: SUB_HEADER_H,
              display: "flex",
              alignItems: "center",
              justifyContent: i === nameIdx ? "flex-start" : "center",
              paddingLeft: i === nameIdx ? 6 : 0,
            }}
          >
            {i === tickerIdx ? bar(34, 8) : i === nameIdx ? bar(56, 8) : null}
          </div>
        ))}
        {metricCols.map((c, i) => (
          <div key={`ch-${i}`} style={{ height: SUB_HEADER_H, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {!c.isSpacer ? bar("55%", 8) : null}
          </div>
        ))}

        {/* Body rows */}
        {Array.from({ length: rowCount }).map((_, r) => {
          const barNameW = 55 + ((r * 13) % 40);
          return (
            <React.Fragment key={`row-${r}`}>
              {renderFrozenRowCells()}
              <div style={{ height: ROW_H, display: "flex", alignItems: "center", paddingLeft: 6, borderTop: ROW_BORDER }}>
                {bar(`${barNameW}%`, 9)}
              </div>
              {metricCols.map((c, i) => (
                <div key={`c-${r}-${i}`} style={{ height: ROW_H, display: "flex", alignItems: "center", justifyContent: "center", borderTop: ROW_BORDER }}>
                  {!c.isSpacer && (r * 7 + i * 3) % 5 !== 0 ? bar("50%", 9) : null}
                </div>
              ))}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
